import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const WHATSAPP_NUMBER = "27659029122";

const ContactPage = () => {
  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi! I'd like to inquire about your spa services.");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FFFCF9]" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-spa-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Get In Touch
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-spa-neutral-800 mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-spa-neutral-500 text-lg max-w-2xl mx-auto">
              Have questions? We'd love to hear from you. Reach out via WhatsApp for the fastest response.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Cards */}
            <div className="space-y-6">
              {[
                { icon: MapPin, title: "Visit Us", content: "32 Kraanvoel Avenue\nKriel" },
                { icon: Phone, title: "Call Us", content: "065 902 9122", link: "tel:0659029122" },
                { icon: Mail, title: "Email Us", content: "krielcolonandbeautyspa@gmail.com", link: "mailto:krielcolonandbeautyspa@gmail.com" },
              ].map((item) => (
                <Card key={item.title} className="rounded-2xl border-0 spa-shadow spa-hover">
                  <CardContent className="p-8 flex items-start gap-6">
                    <div className="bg-spa-pink-100 p-4 rounded-2xl flex-shrink-0">
                      <item.icon className="h-8 w-8 text-spa-pink" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-2">
                        {item.title}
                      </h3>
                      {item.link ? (
                        <a href={item.link} className="text-spa-neutral-600 hover:text-spa-pink-500 transition-colors whitespace-pre-line">
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-spa-neutral-600 whitespace-pre-line">{item.content}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Card className="rounded-2xl border-0 spa-shadow spa-hover">
                <CardContent className="p-8 flex items-start gap-6">
                  <div className="bg-spa-pink-100 p-4 rounded-2xl flex-shrink-0">
                    <Clock className="h-8 w-8 text-spa-pink" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-spa-neutral-800 mb-2">
                      Business Hours
                    </h3>
                    <div className="text-spa-neutral-600 space-y-1">
                      <p>Monday - Friday: 08:00 - 17:00</p>
                      <p>Saturday: 08:00 - 15:00</p>
                      <p className="text-spa-pink">Sunday: Closed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* WhatsApp CTA */}
            <div className="flex flex-col justify-center">
              <Card className="rounded-2xl border-0 bg-spa-pink overflow-hidden">
                <CardContent className="p-12 text-center">
                  <div className="bg-white/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MessageCircle className="h-10 w-10 text-white" />
                  </div>
                  <h2 className="font-heading text-3xl text-white mb-4">
                    Prefer WhatsApp?
                  </h2>
                  <p className="text-white/90 mb-8 max-w-sm mx-auto">
                    Get instant responses to your questions. Our team is ready to help you via WhatsApp.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    className="bg-[#25D366] hover:bg-[#20BA5C] text-white rounded-lg px-10 py-6 text-lg font-semibold shadow-lg"
                    data-testid="contact-whatsapp-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 mr-2">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Chat on WhatsApp
                  </Button>
                </CardContent>
              </Card>

              {/* Social Links */}
              <div className="mt-8 text-center">
                <p className="text-spa-neutral-500 mb-4">Follow us on social media</p>
                <div className="flex justify-center gap-4">
                  {["instagram", "tiktok", "facebook"].map((social) => (
                    <a 
                      key={social}
                      href="#" 
                      className="bg-spa-neutral-100 hover:bg-spa-pink-100 p-3 rounded-full transition-colors"
                      aria-label={social}
                      data-testid={`social-${social}`}
                    >
                      {social === "instagram" && (
                        <svg className="h-6 w-6 text-spa-neutral-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      )}
                      {social === "tiktok" && (
                        <svg className="h-6 w-6 text-spa-neutral-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                        </svg>
                      )}
                      {social === "facebook" && (
                        <svg className="h-6 w-6 text-spa-neutral-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
